/**
 * RevenueChart — Umsatz/Bestellungen im gewählten Zeitraum.
 * Kovalar passen sich automatisch an (Tag / KW / Monat).
 */
import { memo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Panel, PanelEmpty } from './Panel';
import { Skeleton } from '@/components/ui/skeleton';
import { axisEur, eur, num } from './format';

const METRICS = {
  revenue: { label: 'Umsatz', format: eur, axis: axisEur },
  orders: { label: 'Bestellungen', format: num, axis: num },
};
const GRANULARITY_LABEL = { day: 'pro Tag', week: 'pro Kalenderwoche', month: 'pro Monat' };

function ChartTooltip({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-[0_8px_24px_-12px_rgba(23,37,84,0.25)]">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{p.label}</p>
      <p className="font-display text-base font-bold text-slate-900 tabular-nums">{METRICS[metric].format(p[metric])}</p>
      <p className="text-xs text-slate-500 tabular-nums">
        {metric === 'revenue' ? `${num(p.orders)} Bestellungen` : eur(p.revenue)}
      </p>
    </div>
  );
}

function RevenueChart({ loading, series }) {
  const [metric, setMetric] = useState('revenue');
  const points = series?.points || [];
  const empty = points.every((p) => p.orders === 0);

  const toggle = (
    <div role="radiogroup" aria-label="Kennzahl" className="inline-flex gap-1 p-0.5 bg-slate-100 rounded-md">
      {Object.entries(METRICS).map(([id, m]) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={metric === id}
          onClick={() => setMetric(id)}
          className={cn(
            'h-7 px-2.5 rounded text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            metric === id ? 'bg-white text-brand-950 shadow-sm' : 'text-slate-500 hover:text-slate-900'
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );

  return (
    <Panel
      overline={series ? `Verlauf ${GRANULARITY_LABEL[series.granularity]}` : 'Verlauf'}
      title={`${METRICS[metric].label} im Zeitraum`}
      meta={toggle}
      metaWrap
    >
      {loading ? (
        <Skeleton className="h-56 md:h-72 w-full" />
      ) : empty ? (
        <PanelEmpty
          icon={<BarChart3 className="w-8 h-8" />}
          title="Keine Bestellungen in diesem Zeitraum"
          hint="Wählen Sie einen längeren Zeitraum oder erfassen Sie eine Bestellung."
        />
      ) : (
        <div className="h-56 md:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} margin={{ top: 8, right: 4, bottom: 0, left: -8 }} barCategoryGap="18%">
              <CartesianGrid vertical={false} stroke="#E2E8F0" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#64748B', fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#94A3B8', fontSize: 11 }}
                tickFormatter={METRICS[metric].axis}
                allowDecimals={metric === 'revenue'}
                width={44}
              />
              <Tooltip cursor={{ fill: 'rgba(23,37,84,0.05)' }} content={<ChartTooltip metric={metric} />} />
              <Bar dataKey={metric} fill="#172554" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={300} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export default memo(RevenueChart);
